import type { ApolloClient } from '@apollo/client';
import {
    ReportBuilderAttributeRuntimeDataDocument,
    type AttributeValueType,
    type ReportBuilderAttributeRuntimeDataQuery,
    type ReportBuilderAttributeRuntimeDataQueryVariables,
} from '@/graphql/types';
import type { ContextForKind, RenderContextKind } from '../../../elements/types';
import type { BindingId } from '../types';
import {
    formatAttributeLabel,
    getAttributeDisplayLabels,
} from '../../../../attributes/builtInLabels';
import { resolveAttributeRuntimeQueryVariables } from './queryVariables';

// Attribute keys are per entity, so this link cannot declare its args statically.
export const ATTRIBUTES_IMAGE_BINDING_ID = 'attributes.image' as BindingId;

function toAttributeArgOptions(
    data: ReportBuilderAttributeRuntimeDataQuery,
    valueType: AttributeValueType,
    currentArg?: string
): Array<[string, string]> {
    const entries = data.attributesByEntity.filter((entry) => entry.valueType === valueType);
    const labels = getAttributeDisplayLabels(entries, formatAttributeLabel);
    // Keep a saved key binding selectable without showing the same label twice.
    // All other choices use IDs, and the runtime explicitly supports both formats.
    return entries.map((entry, index): [string, string] => [
        entry.key === currentArg ? entry.key : entry.definitionId,
        labels[index],
    ]);
}

export async function resolveAttributeArgOptions(args: {
    client: ApolloClient<object>;
    contextKind: RenderContextKind;
    context: ContextForKind<RenderContextKind>;
    valueType: AttributeValueType;
    currentArg?: string;
}): Promise<Array<[string, string]>> {
    const { client, contextKind, context, valueType, currentArg } = args;
    const { data } = await client.query<
        ReportBuilderAttributeRuntimeDataQuery,
        ReportBuilderAttributeRuntimeDataQueryVariables
    >({
        query: ReportBuilderAttributeRuntimeDataDocument,
        variables: resolveAttributeRuntimeQueryVariables(contextKind, context),
        fetchPolicy: 'cache-first',
    });

    return toAttributeArgOptions(data, valueType, currentArg);
}
